import { defineStore } from 'pinia'
import type { Undefinable } from 'ts-helpers'
import { computed, ref, shallowRef } from 'vue'
import { type BaseSearchFilters, EmptyPagination, type Pagination } from '@/6_shared/model'
import { searchService } from '../api'
import { type Search, SearchAdapter, type SearchDTO } from '../model'

export const useSearch = defineStore('search', () => {
  // search
  const results = shallowRef<Undefinable<Search>>(undefined)
  //pagination
  const paginationEvents = ref<Undefinable<Pagination>>(undefined)
  const isAllEventsLoaded = computed(
    () =>
      !!paginationEvents.value &&
      paginationEvents.value.currentPage >= paginationEvents.value.pagesCount
  )
  const paginationVenues = ref<Undefinable<Pagination>>(undefined)
  const isAllVenuesLoaded = computed(
    () =>
      !!paginationVenues.value &&
      paginationVenues.value.currentPage >= paginationVenues.value.pagesCount
  )
  const updatePagination = (searchResults: SearchDTO) => {
    paginationEvents.value = searchResults.events.pagination
    paginationVenues.value = searchResults.venues.pagination
  }
  //errors
  const searchErrors = ref({
    events: false,
    venues: false
  })
  const resetSearchErrors = () => {
    searchErrors.value.events = false
    searchErrors.value.venues = false
  }
  const setSearchIsFailed = () => {
    searchErrors.value.events = true
    searchErrors.value.venues = true
  }

  /**
   * запись результатов поиска
   * @param searchResults
   */
  const setResults = (searchResults: SearchDTO) => {
    results.value = SearchAdapter(searchResults)
  }

  /**
   * общий поиск
   * @param filters
   * @param nextPage
   */
  const search = async (filters: BaseSearchFilters, nextPage = false) => {
    resetSearchErrors()

    const getFiltersForSearch = (isSearchByVenues: boolean): BaseSearchFilters => {
      const filtersForSearch: BaseSearchFilters = !isSearchByVenues
        ? filters
        : {
            pagination: filters.pagination,
            filter: {}
          }

      if (isSearchByVenues && filters.filter.textQuery)
        filtersForSearch.filter.textQuery = filters.filter.textQuery

      return filtersForSearch
    }
    const searchPromises = [
      searchService.searchByEvents(getFiltersForSearch(false)),
      // в площадках ищем только по текстовому запросу
      searchService.searchByVenues(getFiltersForSearch(true))
    ]

    const [events, venues] = (await Promise.allSettled(searchPromises)).map((item, index) => {
      const { status } = item
      const isEvents = index === 0

      if (status === 'rejected')
        isEvents ? (searchErrors.value.events = true) : (searchErrors.value.venues = true)

      return item.status === 'rejected'
        ? {
            pagination: new EmptyPagination(),
            items: []
          }
        : item.value
    })
    const searchResults = {
      events: events as SearchDTO['events'],
      venues: venues as SearchDTO['venues']
    }

    nextPage ? setNextPageOfResults(searchResults) : setResults(searchResults)
    updatePagination(searchResults)
  }

  /**
   * загрузка следующей страницы
   * @param filters
   */
  const loadNexPage = async (filters: BaseSearchFilters) => {
    await search(
      {
        ...filters,
        pagination: {
          ...filters.pagination,
          currentPage: filters.pagination.currentPage + 1
        }
      },
      true
    )
  }

  /**
   * дописывает следующую страницу поиска
   * @param searchResults
   */
  const setNextPageOfResults = (searchResults: SearchDTO) => {
    if (!results.value) return setResults(searchResults)

    const newResults = SearchAdapter(searchResults)

    results.value = {
      events: [...results.value.events, ...newResults.events],
      venues: [...results.value.venues, ...newResults.venues]
    }
  }

  const resetSearch = () => (results.value = undefined)

  //model
  const searchIsOpen = ref(false)
  const openSearch = () => {
    searchIsOpen.value = true
  }
  const closeSearch = () => (searchIsOpen.value = false)

  return {
    results,
    isAllEventsLoaded,
    isAllVenuesLoaded,
    searchErrors,
    resetSearchErrors,
    setSearchIsFailed,
    search,
    resetSearch,
    loadNexPage,

    searchIsOpen,
    openSearch,
    closeSearch
  }
})
